// Normalize a fluid zone's unit-bearing fields to SI.
//
// Lives in `hydraulic/fluid` because AGENTS.md ("One home per concept") assigns
// density and fluid-zone resolution here. It was briefly under `hydraulic/import`,
// which would have recreated the split ownership this change exists to remove.
//
// WHY THIS IS ITS OWN MODULE (PIPE-142)
//
// The solver reads fluid-zone properties as bare `.value` in a dozen places —
// PumpDevice.ts:605, ControlValveDevice.ts:522, PipeDevice.ts:167,
// DuctDevice.ts:180 and TankPenetrationDevice.ts:220 among them — on the
// assumption that the number is already SI. That assumption holds for a
// Desktop-imported document, because the transformer normalizes it on the way
// in. It does NOT hold for a zone created in the app: `FluidZoneForm` defaults a
// new zone to imperial and stores what it is given.
//
// The result is a document whose zones read `62.3 lb/ft3` and `1.0 cP` while
// every consumer treats them as `kg/m3` and `Pa·s` — density wrong by ~16x and
// viscosity by 1000x, on the same zone.
//
// This logic previously lived inside `desktop-json-transformer.ts`, reachable
// only from the import path. It is here so the app paths can call the SAME
// implementation rather than growing a second one: two normalizers that must
// agree forever is what produced the bug in the first place.
//
// It is fixed for good only when a raw `.value` read cannot compile — a branded
// SI-only type. That is deliberately not this change; it is noted on PIPE-142.

import {
  densityToKgM3,
  temperatureToK,
  temperatureToKStrict,
  viscosityToPaS,
  pressureToPa,
  pressureToPaStrict,
  specificHeatToJkgK,
  specificHeatToJkgKStrict,
} from '../physics/unit-conversions';

type Obj = Record<string, unknown>;

/**
 * What to do when a `{ value, unit }` pair carries no unit.
 *
 * - `assume-si` — treat the bare number as already SI. Correct ONLY for a source
 *   format whose historical contract proves it, which is why the Desktop
 *   transformer has always done this and continues to here.
 * - `throw` — refuse. For a document a user could have produced, a missing unit
 *   is unknown data, and guessing is how a 16x error hides inside a plausible
 *   number. Everything the app itself writes carries a unit.
 */
export type MissingUnitPolicy = 'assume-si' | 'throw';

/** Convert a {value, unit} object to SI in-place. */
export function vwuToSI(
  obj: Obj,
  field: string,
  convert: (v: number, u: string) => number,
  siUnit: string,
  onMissingUnit: MissingUnitPolicy = 'assume-si',
): void {
  const vwu = obj[field];
  if (!vwu || typeof vwu !== 'object') return;
  const f = vwu as Obj;
  if (typeof f['value'] !== 'number') return;
  if (typeof f['unit'] !== 'string' && onMissingUnit === 'throw') {
    throw new Error(
      `vwuToSI: "${field}" has value ${String(f['value'])} but no unit. ` +
      `Refusing to assume ${siUnit} — an unlabelled number is unknown data.`,
    );
  }
  const unit = typeof f['unit'] === 'string' ? f['unit'] : siUnit;
  // ⚠️ VALUE AND UNIT TOGETHER, always. Converting the number while leaving the
  // old unit attached IS the double-conversion bug: a later pass reads `lb/ft3`
  // on an already-converted figure and multiplies by 16.018 again. Replacing
  // both is also what makes this idempotent, since every converter treats its
  // own SI spelling as identity.
  f['value'] = convert(f['value'], unit);
  f['unit'] = siUnit;
}

/**
 * Normalize every unit-bearing field on a fluid zone to SI, in place.
 *
 * Idempotent: a second call converts SI to SI, which each converter defines as
 * identity. Throws (from the converter) on a unit it does not recognise rather
 * than defaulting — a silent default is what masked both the lbm/ft³ and the cP
 * double-conversion bugs before this.
 */
/**
 * {@link normalizeFluidZoneToSI}, collecting failures instead of throwing.
 *
 * ⚠️ READING MUST NOT BRICK A DOCUMENT. The strict converters refuse a unit they
 * do not know, which is right — guessing is what produced the 16x and 1000x bugs.
 * But throwing at a LOAD boundary makes the Model unopenable: import creates it,
 * `loadFromSystem` throws, `openModel` catches and returns false, and the editor
 * shows "Not Found" for a Model that plainly exists.
 *
 * So a field we cannot convert is left EXACTLY as it is — which is what happens
 * today, no worse — and reported. That is still not guessing. Callers that are
 * writing (the zone editor) may treat a non-empty `errors` as a refusal; callers
 * that are reading must surface it and carry on.
 */
export function tryNormalizeFluidZoneToSI(
  fz: Obj,
  onMissingUnit: MissingUnitPolicy = 'assume-si',
): string[] {
  const errors: string[] = [];
  for (const spec of FLUID_ZONE_SI_FIELDS) {
    try {
      normalizeOneField(fz, spec, onMissingUnit);
    } catch (err) {
      errors.push(err instanceof Error ? err.message : String(err));
    }
  }
  return errors;
}

interface FieldSpec {
  field: string;
  siUnit: string;
  lenient: (v: number, u: string) => number;
  strict: (v: number, u: string) => number;
}

/**
 * Every unit-bearing field on a fluid zone, and the SI form each must reach.
 *
 * ONE TABLE, used by both entry points. Two lists that had to agree is the shape
 * of the bug this module exists to remove, so it is not repeated.
 *
 * `pressure` is absent on purpose — see the note in {@link normalizeFluidZoneToSI}.
 */
const FLUID_ZONE_SI_FIELDS: readonly FieldSpec[] = [
  { field: 'temperature', siUnit: 'K', lenient: temperatureToK, strict: temperatureToKStrict },
  { field: 'density', siUnit: 'kg/m³', lenient: densityToKgM3, strict: densityToKgM3 },
  { field: 'viscosity', siUnit: 'Pa·s', lenient: viscosityToPaS, strict: viscosityToPaS },
  { field: 'vapor_pressure', siUnit: 'Pa', lenient: pressureToPa, strict: pressureToPaStrict },
  // Read raw by HeatSourceSinkDevice.ts:301 as J/(kg·K).
  { field: 'specific_heat', siUnit: 'J/(kg·K)', lenient: specificHeatToJkgK, strict: specificHeatToJkgKStrict },
  // These three are typed ValueWithUnit but were historically read as bare
  // `.value` on the strength of a "conventionally Pa/K" comment. The unit is
  // data on every instance — honour it so consumers cannot be wrong. Identity on
  // the converter's SI form, which is all the corpus carries.
  { field: 'critical_pressure', siUnit: 'Pa', lenient: pressureToPa, strict: pressureToPaStrict },
  { field: 'critical_temperature', siUnit: 'K', lenient: temperatureToK, strict: temperatureToKStrict },
  { field: 'bulk_modulus', siUnit: 'Pa', lenient: pressureToPa, strict: pressureToPaStrict },
];

function normalizeOneField(fz: Obj, spec: FieldSpec, onMissingUnit: MissingUnitPolicy): void {
  // ⚠️ STRICT CONVERTERS ON THE APP PATH. `pressureToPa` falls back to psi and
  // `temperatureToK` to degF for ANY unit they do not recognise — an AFT
  // convention that is right for a file format known to be imperial and wrong for
  // a document a user could have authored. `pressureToPaStrict`'s own error says
  // silent fallbacks "masked the 16× density / 1000× viscosity bugs for months".
  //
  // The Desktop path keeps the lenient pair, whose historical contract is exactly
  // what those fallbacks encode.
  const convert = onMissingUnit === 'throw' ? spec.strict : spec.lenient;
  vwuToSI(fz, spec.field, convert, spec.siUnit, onMissingUnit);
}

export function normalizeFluidZoneToSI(
  fz: Obj,
  onMissingUnit: MissingUnitPolicy = 'assume-si',
): void {
  for (const spec of FLUID_ZONE_SI_FIELDS) normalizeOneField(fz, spec, onMissingUnit);

  // ⚠️ THE ZONE'S OPERATING PRESSURE IS DELIBERATELY NOT NORMALISED HERE.
  //
  // It is a real bug: the form's "Add operating pressure" defaults to 14.696
  // psia and `ControlValveDevice.ts:1030` reads `zone.pressure?.value` through a
  // type with no unit field at all, so it is structurally assumed Pa — ~6895x.
  //
  // But it cannot be fixed on the app path alone, and it must not be fixed on
  // both. All 180 zones in the Desktop corpus carry `Pa g`, which `pressureToPa`
  // reads as GAUGE and converts by adding an atmosphere — shifting solver input
  // on every Desktop model. An earlier revision tried normalising only under the
  // strict policy to dodge that; it does not work, because a Desktop-imported
  // system is handed to `loadFromSystem`, which normalises again under strict
  // (UploadPage.tsx:277). There is no policy split that survives that path.
  //
  // Settling it means deciding whether the solver wants gauge or absolute — the
  // P₁ at ControlValveDevice.ts:1030 feeds a choked-flow curve whose own comment
  // calls it desktop's absolute `fluid_ptr->pressure()`, so converting is likely
  // right — and then proving it with a desktop-parity run. That is a distinct
  // defect from unit STORAGE, which is what this module is for.
}

/**
 * A copy of `system` whose every fluid zone is SI. Does NOT mutate the input —
 * the caller is usually handing the result straight to a React store, where an
 * in-place edit of the previous system would not re-render.
 *
 * Defaults to the strict missing-unit policy. The Desktop import path passes
 * 'assume-si' to preserve the contract it has always relied on.
 */
export interface FluidZoneNormalizationResult<T> {
  system: T;
  /**
   * True when at least one number or unit actually moved.
   *
   * The caller needs this because a document normalised on load was SOLVED with
   * the old numbers: its persisted `edge.calc` / `vertex.calc` blocks are the
   * answer for 62.3 kg/m³, and presenting them beside a now-998 kg/m³ Model
   * shows the Engineer stale results as current. A Model already in SI must NOT
   * be marked stale, which is why this is reported rather than assumed.
   */
  changed: boolean;
  /**
   * One message per field that could not be converted, left untouched.
   *
   * Never empty-means-fine by omission: a READING caller must surface these and
   * carry on (throwing makes the Model unopenable — see
   * {@link tryNormalizeFluidZoneToSI}), while a WRITING caller may treat any
   * entry as a refusal, because the user is right there and can fix the unit.
   */
  errors: string[];
}

/**
 * A copy of `system` whose every fluid zone is SI. Does NOT mutate the input —
 * the caller is usually handing the result straight to a React store, where an
 * in-place edit of the previous system would not re-render.
 *
 * Defaults to the strict missing-unit policy. The Desktop import path passes
 * 'assume-si' to preserve the contract it has always relied on.
 */
export function normalizeSystemFluidZonesToSI<T extends { fluid_zones?: unknown }>(
  system: T,
  onMissingUnit: MissingUnitPolicy = 'throw',
): FluidZoneNormalizationResult<T> {
  const zones = system.fluid_zones;
  if (!Array.isArray(zones) || zones.length === 0) return { system, changed: false, errors: [] };

  let changed = false;
  const errors: string[] = [];
  const next = zones.map((z): unknown => {
    if (!z || typeof z !== 'object') return z;
    const copy = { ...(z as Obj) };
    // The nested {value, unit} objects are still shared with the input after a
    // shallow copy, and vwuToSI writes in place — so clone each one it may touch.
    for (const k of Object.keys(copy)) {
      const v = copy[k];
      if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in (v as Obj)) {
        copy[k] = { ...(v as Obj) };
      }
    }
    // Collecting rather than throwing: one unconvertible field must not make the
    // whole Model unopenable. The field keeps its current value, which is exactly
    // what happens today.
    const zoneId = typeof (copy as Obj)['id'] === 'string' ? String((copy as Obj)['id']) : '<unnamed zone>';
    for (const message of tryNormalizeFluidZoneToSI(copy, onMissingUnit)) {
      errors.push(`${zoneId}: ${message}`);
    }
    for (const k of Object.keys(copy)) {
      const before = (z as Obj)[k];
      const after = copy[k];
      if (!before || typeof before !== 'object' || !after || typeof after !== 'object') continue;
      const b = before as Obj;
      const a = after as Obj;
      if (!('value' in b)) continue;
      // ⚠️ THE VALUE ONLY, never the unit. Normalising `m^-3 kg` to `kg/m³`
      // relabels without moving the number, and every zone in the Desktop corpus
      // is spelled that way — treating a relabel as a change would mark the
      // results of every imported Model stale merely for opening it.
      if (b['value'] !== a['value']) changed = true;
    }
    return copy;
  });
  return { system: { ...system, fluid_zones: next }, changed, errors };
}
